import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { formatGMV } from 'lib/utils/FormatUtils';
import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_XS,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  SHADOW_XS,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

const DASH = '—';

/**
 * Image sources tried in priority order. When one URL fails to load (404,
 * CORS, etc.) the next one is used. The local placeholder SVG is the final
 * fallback and is bundled with the app so it's guaranteed to render.
 */
function imageCandidates(product) {
  return [
    product.thumbnail_url,
    product.default_image_url,
    product.propagated_thumbnail_image_url,
    placeholderProductImage,
  ].filter(Boolean);
}

/**
 * ProductImage — img with a fallback chain. On load error we advance to the
 * next candidate URL. Reset the index whenever the product changes (e.g. on
 * page change / sort) so we re-evaluate the chain from the top.
 */
function ProductImage({ product, className }) {
  const candidates = useMemo(() => imageCandidates(product), [product]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [product]);

  const handleError = () => {
    if (idx < candidates.length - 1) setIdx(idx + 1);
  };

  return <img src={candidates[idx]} alt="" className={className} onError={handleError} />;
}

ProductImage.propTypes = {
  product: PropTypes.object.isRequired,
  className: PropTypes.string,
};

ProductImage.defaultProps = { className: undefined };

function fmtCount(value) {
  if (value == null) return DASH;
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : DASH;
}

function fmtPct(value) {
  if (value == null) return DASH;
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(1)}%` : DASH;
}

function fmtScore(value) {
  if (value == null) return DASH;
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n)}%` : DASH;
}

function StatRow({ label, value }) {
  return (
    <div className={css(styles.stat)}>
      <span className={css(styles.statLabel)}>{label}</span>
      <span className={css(styles.statValue)}>{value}</span>
    </div>
  );
}

StatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

/**
 * BetaProductCard — beta-styled product tile that matches the mockup
 * (UPB Viewer - Products.html). Shows image on top + name + vendor + CID +
 * a stacked stat block (Annualized GMV, DPs, Market Share, eCom Score,
 * Attribute Score). Missing payload fields render as em-dash.
 */
function BetaProductCard({ product, onClick }) {
  const name = product.name || DASH;
  // The /search payload exposes `brand` / `propagated_brand`. With
  // with_vendor_aggregates=true the handler also injects `brand_name` —
  // preferring the manufacturer-verified brand (MFC) before falling back to
  // vendor-derived. This is the "Brand / MFC" line in the spec.
  const brand = product.brand || product.propagated_brand || product.brand_name || null;
  const cid = product.gtin || product.upc || null;
  // MPN (Manufacturer Part Number) — joined from GlobalProductManufacturer.
  const mpn = product.mpn || null;

  // When the page is filtered to an L4, the handler injects two fields via
  // GMVHandler.get_l5_gmv_percentages:
  //   invoice_and_ordering_gmv_percentage → the annualized $ GMV amount
  //   gmv_percentage                      → market-share % within the L4
  const gmvRaw = product.invoice_and_ordering_gmv_percentage || product.annualized_gmv;
  const gmv = gmvRaw ? formatGMV(gmvRaw) : DASH;
  // _market_share is the derived per-L4 share (product GMV / L4 total GMV)
  // computed by BetaProductsPage. Falls back to the legacy gmv_percentage
  // field on the rare chance the page didn't annotate it.
  const marketShareRaw =
    product._market_share != null ? product._market_share : product.gmv_percentage;
  const marketShare = fmtPct(marketShareRaw);

  // Score fields not yet returned by /search; we surface product_score as the
  // attribute score (matches the non-beta UPB card's fallback chain).
  const ecom = fmtScore(product.p0_score);
  const attr = fmtScore(product.fsa_score || product.product_score);

  // DPs = distinct distributors selling the product in-window (from the MFR GMV
  // query). Annualized Units is intentionally omitted: no reliable source — the
  // quantities mix units of measure (cases / eaches / pounds), so a summed count
  // isn't meaningful without UOM normalization.
  const dps = fmtCount(product.dp_count);

  return (
    <button
      type="button"
      className={css(styles.card)}
      onClick={() => onClick && onClick(product)}
      title={name}
    >
      <div className={css(styles.imageBox)}>
        <ProductImage product={product} className={css(styles.image)} />
      </div>
      <div className={css(styles.body)}>
        <div className={css(styles.name)}>{name}</div>
        {/* Always render brand + CID + MPN rows (with em-dash fallback) so
            cards in the same grid row keep matching heights. */}
        <div className={css(styles.brand)}>{brand || DASH}</div>
        <div className={css(styles.cid)}>{cid ? `CID: ${cid}` : DASH}</div>
        <div className={css(styles.cid)}>{mpn ? `MPN: ${mpn}` : DASH}</div>
        <div className={css(styles.stats)}>
          <StatRow label="Annualized GMV" value={gmv} />
          <StatRow label="DPs" value={dps} />
          <StatRow label="L4 Market Share" value={marketShare} />
          <StatRow label="eCom Score" value={ecom} />
          <StatRow label="Attribute Score" value={attr} />
        </div>
      </div>
    </button>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    ':hover': {
      borderColor: PRIMARY_COLOR,
      boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}, ${SHADOW_XS}`,
    },
  },

  imageBox: {
    // Fixed height + overflow hidden — aspect-ratio doesn't reliably constrain
    // a flex container when the inner <img> has a tall intrinsic ratio
    // (measured 388px instead of 218px on tall keg photos).
    height: 200,
    overflow: 'hidden',
    backgroundColor: BG_2,
    borderBottom: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    flexShrink: 0,
  },

  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },

  body: {
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    // Fill remaining card height so the stats block can pin to the bottom.
    flex: 1,
  },

  name: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  brand: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  cid: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    borderTop: `1px solid ${BORDER_2}`,
    paddingTop: 8,
    // Push the stat block to the bottom of the body — keeps the divider line
    // anchored at the same Y across cards in the same row, even when some
    // cards have fewer brand/CID details above.
    marginTop: 'auto',
  },

  stat: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },

  statLabel: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
  },

  statValue: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: FONT_SIZE_XS,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },
});

BetaProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

BetaProductCard.defaultProps = {
  onClick: null,
};

export default BetaProductCard;
